// src/input/controls.ts
// Touch / mouse / keyboard input (spec §6). Two pointer idioms: (a) drag/swipe a block to pick the
// block + a 4-way-snapped direction; (b) tap a block, then tap one of the direction arrows drawn
// around it. Keyboard: arrow keys move the selected block, Tab/Shift+Tab cycle selection (and release
// focus past the ends so it is never a trap), Z = undo, R = reset, Escape = deselect. Hit-testing maps
// pointer → cell → block via the engine's blockOccupies. Large, forgiving hit areas.

import type { GameState, ReachResult } from '../game/rules';
import type { BlockId, Cell, Direction } from '../game/types';
import type { CanvasView } from '../render/canvas';
import { blockBounds, blockOccupies } from '../game/block';

export interface ControlCallbacks {
  /** Current play state (history.present). */
  getState(): GameState;
  /** Currently selected block id, or null. */
  getSelected(): BlockId | null;
  /** True while an animation is running — input is ignored. */
  isBusy(): boolean;
  /** Set/clear the selected block. */
  onSelect(id: BlockId | null): void;
  /** Keyboard move: slide the selected block one cell in `dir`. */
  onMove(id: BlockId, dir: Direction, distance: number): void;
  /** Free-roam: every anchor `id` can reach via a clear path, plus its exit anchor/edge. */
  getReach(id: BlockId): ReachResult;
  /** Live drag preview: show `id` ghosted at `anchor` (null clears the preview). */
  onDragPreview(id: BlockId, anchor: Cell | null): void;
  /** Drop: commit `id` to `anchor` (exit === true ⇒ leave through its gate). */
  onDrop(id: BlockId, anchor: Cell, exit: boolean): void;
  onUndo(): void;
  onReset(): void;
  /** Drives the visible keyboard focus ring (true == keyboard modality). */
  setKeyboardActive(active: boolean): void;
}

interface DragState {
  readonly pointerId: number;
  readonly blockId: BlockId;
  readonly startX: number;
  readonly startY: number;
  readonly reach: ReachResult;
  readonly startAnchor: Cell;
  /** Half-extents of the piece in cells (for centring the piece under the finger). */
  readonly halfRows: number;
  readonly halfCols: number;
  previewAnchor: Cell;
  exit: boolean;
}

type Modality = 'pointer' | 'keyboard';

export class Controls {
  private readonly canvas: HTMLCanvasElement;
  private readonly view: CanvasView;
  private readonly cb: ControlCallbacks;
  private drag: DragState | null = null;
  private modality: Modality = 'pointer';

  // Bound handlers (retained for removal).
  private readonly onPointerDown: (e: PointerEvent) => void;
  private readonly onPointerMove: (e: PointerEvent) => void;
  private readonly onPointerUp: (e: PointerEvent) => void;
  private readonly onPointerCancel: (e: PointerEvent) => void;
  private readonly onKeyDown: (e: KeyboardEvent) => void;
  private readonly onFocus: () => void;
  private readonly onBlur: () => void;
  private readonly onDocPointer: () => void;
  private readonly onDocKey: () => void;

  constructor(canvas: HTMLCanvasElement, view: CanvasView, cb: ControlCallbacks) {
    this.canvas = canvas;
    this.view = view;
    this.cb = cb;

    this.onPointerDown = (e) => this.handlePointerDown(e);
    this.onPointerMove = (e) => this.handlePointerMove(e);
    this.onPointerUp = (e) => this.handlePointerUp(e);
    this.onPointerCancel = (e) => this.handlePointerCancel(e);
    this.onKeyDown = (e) => this.handleKeyDown(e);
    this.onFocus = () => {
      const keyboard = this.modality === 'keyboard';
      this.cb.setKeyboardActive(keyboard);
      // Tab-into-board selects the first block so arrow keys have an immediate target (§6).
      if (keyboard && this.cb.getSelected() === null) {
        const ids = this.sortedIds();
        if (ids.length > 0) {
          this.cb.onSelect(ids[0] ?? null);
        }
      }
    };
    this.onBlur = () => this.cb.setKeyboardActive(false);
    this.onDocPointer = () => {
      this.modality = 'pointer';
    };
    this.onDocKey = () => {
      this.modality = 'keyboard';
    };

    canvas.addEventListener('pointerdown', this.onPointerDown);
    canvas.addEventListener('pointermove', this.onPointerMove);
    canvas.addEventListener('pointerup', this.onPointerUp);
    canvas.addEventListener('pointercancel', this.onPointerCancel);
    canvas.addEventListener('keydown', this.onKeyDown);
    canvas.addEventListener('focus', this.onFocus);
    canvas.addEventListener('blur', this.onBlur);
    document.addEventListener('pointerdown', this.onDocPointer, true);
    document.addEventListener('keydown', this.onDocKey, true);
  }

  dispose(): void {
    const c = this.canvas;
    c.removeEventListener('pointerdown', this.onPointerDown);
    c.removeEventListener('pointermove', this.onPointerMove);
    c.removeEventListener('pointerup', this.onPointerUp);
    c.removeEventListener('pointercancel', this.onPointerCancel);
    c.removeEventListener('keydown', this.onKeyDown);
    c.removeEventListener('focus', this.onFocus);
    c.removeEventListener('blur', this.onBlur);
    document.removeEventListener('pointerdown', this.onDocPointer, true);
    document.removeEventListener('keydown', this.onDocKey, true);
  }

  // ---- pointer ----

  private handlePointerDown(e: PointerEvent): void {
    if (this.cb.isBusy()) {
      return;
    }
    // Make the canvas focusable target for subsequent keyboard play (no ring for mouse).
    this.modality = 'pointer';
    // Clear any stale keyboard focus ring when switching to pointer, even if focus doesn't change.
    this.cb.setKeyboardActive(false);
    if (document.activeElement !== this.canvas) {
      this.canvas.focus({ preventScroll: true });
    }

    // Hit-test a block, select it, and begin a free-roam drag.
    const blockId = this.blockAtPoint(e.clientX, e.clientY);
    if (blockId === null) {
      this.cb.onSelect(null);
      return;
    }
    const block = this.cb.getState().blocks.find((b) => b.id === blockId);
    if (block === undefined) {
      return;
    }
    e.preventDefault();
    this.cb.onSelect(blockId);
    const bounds = blockBounds(block);
    this.drag = {
      pointerId: e.pointerId,
      blockId,
      startX: e.clientX,
      startY: e.clientY,
      reach: this.cb.getReach(blockId),
      startAnchor: { row: block.anchor.row, col: block.anchor.col },
      halfRows: (bounds.max.row - bounds.min.row + 1) / 2,
      halfCols: (bounds.max.col - bounds.min.col + 1) / 2,
      previewAnchor: { row: block.anchor.row, col: block.anchor.col },
      exit: false,
    };
    try {
      this.canvas.setPointerCapture(e.pointerId);
    } catch {
      // Capture is best-effort.
    }
  }

  private handlePointerMove(e: PointerEvent): void {
    const d = this.drag;
    if (d === null || e.pointerId !== d.pointerId) {
      return;
    }
    const f = this.view.clientToCellF(e.clientX, e.clientY);
    if (f === null) {
      return;
    }
    const board = this.cb.getState().board;
    // Prefer an exit when the finger is pushed off-board on the gate's edge and the piece can exit.
    const wantsExit =
      d.reach.exitAnchor !== null &&
      d.reach.exitDir !== null &&
      isExitIntent(f.row, f.col, board.rows, board.cols, d.reach.exitDir);
    const target: Cell =
      wantsExit && d.reach.exitAnchor !== null
        ? d.reach.exitAnchor
        : nearestAnchor(f.row, f.col, d.reach.anchors, d.halfRows, d.halfCols);

    if (target.row !== d.previewAnchor.row || target.col !== d.previewAnchor.col || wantsExit !== d.exit) {
      d.previewAnchor = target;
      d.exit = wantsExit;
      this.cb.onDragPreview(d.blockId, target);
    }
  }

  private handlePointerUp(e: PointerEvent): void {
    const d = this.drag;
    if (d === null || e.pointerId !== d.pointerId) {
      return;
    }
    this.drag = null;
    try {
      this.canvas.releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
    if (this.cb.isBusy()) {
      this.cb.onDragPreview(d.blockId, null);
      return;
    }
    if (d.exit && d.reach.exitAnchor !== null) {
      this.cb.onDrop(d.blockId, d.reach.exitAnchor, true);
      return;
    }
    if (d.previewAnchor.row !== d.startAnchor.row || d.previewAnchor.col !== d.startAnchor.col) {
      this.cb.onDrop(d.blockId, d.previewAnchor, false);
      return;
    }
    // A tap (no real move): keep the selection, clear any stray preview.
    this.cb.onDragPreview(d.blockId, null);
  }

  private handlePointerCancel(e: PointerEvent): void {
    const d = this.drag;
    if (d !== null && d.pointerId === e.pointerId) {
      this.drag = null;
      this.cb.onDragPreview(d.blockId, null);
    }
  }

  private blockAtPoint(clientX: number, clientY: number): BlockId | null {
    const cell = this.view.clientToCell(clientX, clientY);
    if (cell === null) {
      return null;
    }
    for (const b of this.cb.getState().blocks) {
      if (blockOccupies(b, cell)) {
        return b.id;
      }
    }
    return null;
  }

  // ---- keyboard ----

  private handleKeyDown(e: KeyboardEvent): void {
    const key = e.key;

    // Undo / reset never need a selection. Skip when a browser/OS combo is intended.
    if (!e.ctrlKey && !e.metaKey && !e.altKey) {
      if (key === 'z' || key === 'Z') {
        e.preventDefault();
        this.modality = 'keyboard';
        this.cb.setKeyboardActive(true);
        this.cb.onUndo();
        return;
      }
      if (key === 'r' || key === 'R') {
        e.preventDefault();
        this.modality = 'keyboard';
        this.cb.setKeyboardActive(true);
        this.cb.onReset();
        return;
      }
    }
    if (key === 'Escape') {
      e.preventDefault();
      this.cb.onSelect(null);
      return;
    }

    if (key === 'Tab') {
      this.handleTab(e);
      return;
    }

    const dir = arrowKeyToDirection(key);
    if (dir !== null) {
      if (this.cb.isBusy()) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      this.modality = 'keyboard';
      this.cb.setKeyboardActive(true);
      const sel = this.cb.getSelected();
      const ids = this.sortedIds();
      if (sel === null) {
        // Nothing selected yet: select the first block (no move) to orient the player.
        if (ids.length > 0) {
          this.cb.onSelect(ids[0] ?? null);
        }
        return;
      }
      this.cb.onMove(sel, dir, 1);
    }
  }

  /** Tab/Shift+Tab cycle selection; at the ends, deselect and let focus leave (never a trap). */
  private handleTab(e: KeyboardEvent): void {
    const ids = this.sortedIds();
    if (ids.length === 0) {
      return; // allow default focus movement
    }
    this.modality = 'keyboard';
    this.cb.setKeyboardActive(true);
    const sel = this.cb.getSelected();
    const cur = sel === null ? -1 : ids.indexOf(sel);
    const forward = !e.shiftKey;

    if (cur === -1) {
      // Entering selection: first (forward) or last (backward).
      const id = forward ? ids[0] : ids[ids.length - 1];
      e.preventDefault();
      this.cb.onSelect(id ?? null);
      return;
    }
    const next = forward ? cur + 1 : cur - 1;
    if (next >= 0 && next < ids.length) {
      e.preventDefault();
      this.cb.onSelect(ids[next] ?? null);
      return;
    }
    // Past an end: release the selection and let the browser move focus onward.
    this.cb.onSelect(null);
  }

  private sortedIds(): readonly BlockId[] {
    return this.cb.getState().blocks.map((b) => b.id).sort((a, b) => a - b);
  }
}

// ---- helpers ----

/**
 * Pure helper: of the reachable `anchors`, pick the one whose piece-centre lands closest to the
 * finger (given in fractional cell coords). `halfRows/halfCols` centre the piece under the finger.
 * Exported for unit tests. `anchors` is non-empty (always includes the piece's own anchor).
 */
export function nearestAnchor(
  fRow: number,
  fCol: number,
  anchors: readonly Cell[],
  halfRows: number,
  halfCols: number,
): Cell {
  let best = anchors[0]!;
  let bestD = Infinity;
  for (const a of anchors) {
    const dr = fRow - (a.row + halfRows);
    const dc = fCol - (a.col + halfCols);
    const dist = dr * dr + dc * dc;
    if (dist < bestD) {
      bestD = dist;
      best = a;
    }
  }
  return best;
}

/**
 * Pure helper: true when the finger (fractional cell coords) is pushed off the board on the gate's
 * exit edge — the gesture that flings a piece out through its gate. A small overhang (0.35 cell)
 * past the edge avoids accidental exits while resting flush against it.
 */
export function isExitIntent(
  fRow: number,
  fCol: number,
  rows: number,
  cols: number,
  exitDir: Direction,
): boolean {
  const over = 0.35;
  switch (exitDir) {
    case 'up':
      return fRow < -over;
    case 'down':
      return fRow > rows + over;
    case 'left':
      return fCol < -over;
    case 'right':
      return fCol > cols + over;
    default: {
      const _exhaustive: never = exitDir;
      return _exhaustive;
    }
  }
}

function arrowKeyToDirection(key: string): Direction | null {
  switch (key) {
    case 'ArrowUp':
      return 'up';
    case 'ArrowDown':
      return 'down';
    case 'ArrowLeft':
      return 'left';
    case 'ArrowRight':
      return 'right';
    default:
      return null;
  }
}
